import type { Archive, ChatMessage, TurnInputMetadata } from "../../domain/types";
import { restoreTurnCheckpoint } from "../../services/db";

function targetMessage(archive: Archive, messageId: string) {
  const index = archive.messages.findIndex((message) => message.id === messageId);
  if (index < 0) throw new Error("找不到目标消息");
  return { index, message: archive.messages[index]! };
}

function nextTurnCheckpoint(
  messages: readonly ChatMessage[],
  index: number,
  currentSnapshotId: string,
) {
  for (let i = index + 1; i < messages.length; i += 1) {
    const candidate = messages[i]?.snapshotId;
    if (candidate && candidate !== currentSnapshotId) return candidate;
  }
  return null;
}

export function canRewindMessage(message: ChatMessage) {
  return message.role !== "system" && Boolean(message.snapshotId);
}

export async function rewindToMessage(archive: Archive, messageId: string) {
  const { index, message } = targetMessage(archive, messageId);
  if (!message.snapshotId) throw new Error("该旧消息没有可用的回溯保存点");
  const latestAssistant = [...archive.messages]
    .reverse()
    .find((candidate) => candidate.role === "assistant");
  const checkpointId =
    message.role === "assistant" && latestAssistant?.id !== message.id
      ? nextTurnCheckpoint(archive.messages, index, message.snapshotId) ??
        message.snapshotId
      : message.snapshotId;
  return restoreTurnCheckpoint(checkpointId);
}

export async function prepareRewindAndResend(
  archive: Archive,
  messageId: string,
  editedContent?: string,
): Promise<{ archive: Archive; replay: TurnInputMetadata }> {
  const { index, message } = targetMessage(archive, messageId);
  if (!message.snapshotId) throw new Error("该旧消息没有可用的回溯保存点");
  let source = message;
  if (message.role === "assistant") {
    for (let i = index - 1; i >= 0; i -= 1) {
      if (archive.messages[i]?.role === "user") {
        source = archive.messages[i]!;
        break;
      }
    }
  }
  const stored = source.turnInput ?? message.turnInput;
  const content = editedContent?.trim() || stored?.content || source.content.trim();
  if (!content) throw new Error("没有找到可重发的玩家输入");
  const restored = await restoreTurnCheckpoint(message.snapshotId);
  return {
    archive: restored,
    replay: {
      content,
      ...(editedContent?.trim() ? {} : stored?.autoContinue ? { autoContinue: stored.autoContinue } : {}),
    },
  };
}
